import capitalize from 'lodash/capitalize';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import { CircularRates } from 'src/shared/components/CircularRates';
import { CircularRatesWrapper } from 'src/shared/components/styles';

import { ProgramFeedback } from '../../cpProgram/weekFour/models/weekFour';
import { ProgramEnum, ProgramWeek } from '../../models/programs';
import { GenericProgramStep } from '../../models/programsGeneric';
import { getPreviousWeekData } from '../../selectors/programsSelectors';
import {
    changeFeedbackField,
    fetchProgramFeedback,
    fetchWeekFeedback,
    submitFeedback,
} from '../actions/feedbackActions';
import { WeekFeedback } from '../models/feedback';
import {
    getFeedbackOrProgramFeedback,
    isCurrentFeedbackStepValid,
    isStepProgramFeedback,
} from '../selectors/feedbackSelectors';

interface StateProps {
    previousWeek: ProgramWeek | undefined;
    isProgramFeedback: boolean;
    feedback: WeekFeedback | ProgramFeedback | undefined;
    isDataValid?: boolean;
}

interface ActionsProps {
    fetchWeekFeedback: typeof fetchWeekFeedback;
    fetchProgramFeedback: typeof fetchProgramFeedback;
    changeFeedbackField: typeof changeFeedbackField;
    submitFeedback: typeof submitFeedback;
}

interface Props extends StepButtonsProps, StateProps, ActionsProps, WithTranslation {
    programId: ProgramEnum;
    programTitle: string;
    week: ProgramWeek;
    stepId: GenericProgramStep;
}

class FeedbackStepContainer extends React.Component<Props> {
    private changeFeedbackField = (value: string | number, field: string) =>
        this.props.changeFeedbackField({
            programId: this.props.programId,
            weekId: this.props.isProgramFeedback ? undefined : this.props.previousWeek!.id,
            field,
            value,
        });

    private handleRateChange = (value: number) => this.changeFeedbackField(value, 'rating');

    private handleRecommendationProbabilityChange = (value: number) =>
        this.changeFeedbackField(value, 'recommendationProbability');

    private handleAdditionalFeedbackChange = ({ target }: React.ChangeEvent<HTMLTextAreaElement>) =>
        this.changeFeedbackField(target.value, 'content');

    private handleSubmit = () => {
        const { programId, isProgramFeedback, previousWeek, feedback, isDataValid } = this.props;

        if (!isDataValid || !feedback) {
            return;
        }

        this.props.submitFeedback({
            programId,
            weekId: isProgramFeedback ? undefined : previousWeek!.id,
            feedback,
        });

        this.props.goToNextStep();
    };

    public componentDidMount(): void {
        const { programId, previousWeek, isProgramFeedback } = this.props;

        if (isProgramFeedback) {
            this.props.fetchProgramFeedback(programId);
        }

        if (previousWeek) {
            this.props.fetchWeekFeedback({
                programId,
                weekId: previousWeek.id,
            });
        }
    }

    private t = (path: string, options?: any) => this.props.t(`steps.feedback.${path}`, options);

    public render(): JSX.Element | null {
        const { programTitle, previousWeek, feedback, isProgramFeedback } = this.props;

        const headingText = isProgramFeedback
            ? this.t('program.heading', { programTitle })
            : this.t('week.heading', {
                  previousWeekNumber: previousWeek!.readableWeekNumber,
                  previousWeekTitle: capitalize(previousWeek!.title),
              });

        const howWouldYouRateText = isProgramFeedback
            ? this.t('program.howWouldYouRate')
            : this.t('week.howWouldYouRate', {
                  previousWeekNumber: previousWeek!.readableWeekNumber,
                  programTitle,
              });

        return previousWeek ? (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>{headingText}</S.StepHeading>
                        <S.StepBoxWrapper>
                            <S.FeedbackHeader>{howWouldYouRateText}</S.FeedbackHeader>
                            <S.StyledRate
                                value={feedback && feedback.rating}
                                onChange={this.handleRateChange}
                            />
                        </S.StepBoxWrapper>
                        {isProgramFeedback && (
                            <S.StepBoxWrapper>
                                <S.FeedbackHeader style={{ marginBottom: '2rem' }}>
                                    {this.t('program.howLikelyAreYouToRecommend')}
                                </S.FeedbackHeader>
                                <CircularRatesWrapper>
                                    <CircularRates
                                        range={10}
                                        currentRate={
                                            (feedback &&
                                                (feedback as ProgramFeedback)
                                                    .recommendationProbability) ||
                                            0
                                        }
                                        minLabel={this.t('program.minLabel')}
                                        maxLabel={this.t('program.maxLabel')}
                                        onRateChange={this.handleRecommendationProbabilityChange}
                                    />
                                </CircularRatesWrapper>
                            </S.StepBoxWrapper>
                        )}
                        <S.StepBoxWrapper>
                            <S.FeedbackHeader>{this.t('anythingElse')}</S.FeedbackHeader>
                            <S.TextArea
                                placeholder={previousWeek.feedbackPlaceholder}
                                value={feedback && feedback.content}
                                onChange={this.handleAdditionalFeedbackChange}
                                autosize={{ minRows: 3, maxRows: 8 }}
                            />
                        </S.StepBoxWrapper>
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    hasNextStep
                    nextDisabled={!this.props.isDataValid}
                />
            </>
        ) : null;
    }
}

const mapStateToProps = (state: AppState, { programId, week, stepId }: Props): StateProps => ({
    previousWeek: getPreviousWeekData(state, programId, week.id),
    isProgramFeedback: isStepProgramFeedback(stepId),
    feedback: getFeedbackOrProgramFeedback(state, programId, week.id, stepId),
    isDataValid: isCurrentFeedbackStepValid(state, programId, week.id, stepId),
});

export const FeedbackStep = withTranslation('programs')(
    connect<StateProps, ActionsProps>(mapStateToProps, {
        fetchWeekFeedback,
        fetchProgramFeedback,
        changeFeedbackField,
        submitFeedback,
    })(FeedbackStepContainer),
);
